import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useSessionDataWithRovoExperiment } from '@confluence/session-data';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { useLoomAdControlState } from '@confluence/loom-utils/entry-points/useLoomAdControlState';

// eslint-disable-next-line import/no-cycle
import { READ_TIME_DROPDOWN_ENTRY_POINT_ID } from '../ui/ReadTimeDropdownLoomScriptExtension';
import { RovoChatLoomScriptCtaListener } from '../RovoChatLoomScriptCtaListener';
import { isPageCreatedIn30Days } from '../utils/dateUtils';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

export const READTIME_LOOM_SCRIPT_FEATURE_DISCOVERY_MESSAGE_ID =
	'readtime-loom-script-feature-discovery' as const;

interface LoomScriptReadTimeContextProps {
	isLoomScriptReadTimeExtensionEnabled: boolean;
	isLoomScriptReadTimeButtonNudgeShown: boolean;
	isLoomScriptReadTimeSpotlightShown: boolean;
	notInterestedInLoomCallback: () => void;
	updateLoomScriptReadTimeEligibilityForPage: (
		contentNode: any,
		readTime: number | null,
		isPageWithLoomVideos: boolean | undefined,
	) => void;
	hideLoomScriptReadTimeNudgeAndShowSpotlight: () => void;
	dismissLoomScriptReadTimeSpotlight: () => void;
}

const LoomScriptReadTimeContext = createContext<LoomScriptReadTimeContextProps>({
	isLoomScriptReadTimeExtensionEnabled: false,
	isLoomScriptReadTimeButtonNudgeShown: false,
	isLoomScriptReadTimeSpotlightShown: false,
	notInterestedInLoomCallback: () => {},
	updateLoomScriptReadTimeEligibilityForPage: () => {},
	hideLoomScriptReadTimeNudgeAndShowSpotlight: () => {},
	dismissLoomScriptReadTimeSpotlight: () => {},
});

export const useLoomScriptReadTimeContext = () => useContext(LoomScriptReadTimeContext);

interface LoomScriptReadTimeContextProviderProps {
	isLoomAIScriptExperimentEnabled: boolean;
	children: React.ReactNode;
	contentId: string;
}

/**
 * Provides a context for the Loom Script Read Time Summary Video section, Nudge, and Spotlight.
 *
 * Summary Video section is shown if the page meets the basic criteria
 * Nudge is shown if the page also satisfies a minimum read time, and the spotlight has not already been dismissed
 * Nudge is hidden when it is clicked on, and the spotlight shown in the dropdown
 * When the spotlight is dismissed, mark it as dismissed in local storage, so the nudge will not show again
 */
const LoomScriptReadTimeEnabledContextProvider = ({
	children,
	contentId,
}: LoomScriptReadTimeContextProviderProps) => {
	const isLivePage = useIsCurrentPageLive();
	const { entryPointVariant } = useLoomEntryPointVariant();
	const { isRovoEnabled, userId } = useSessionDataWithRovoExperiment();

	const { isInterestedInLoom, notInterestedInLoomCallback } = useLoomAdControlState({
		touchpoint: READ_TIME_DROPDOWN_ENTRY_POINT_ID,
	});

	const [isLoomScriptReadTimeExtensionEnabled, setIsLoomScriptReadTimeExtensionEnabled] =
		useState(false);

	const [isMinimumReadTimeMet, setIsMinimumReadTimeMet] = useState(false);
	const [isPageCreatedIn30DaysState, setIsPageCreatedIn30DaysState] = useState(false);

	/**
	 * All these states are mutually exclusive
	 */
	const [featureDiscoveryState, setFeatureDiscoveryState] = useState<
		'hidden' | 'nudge' | 'spotlight'
	>('hidden');

	/**
	 * Make sure the timeout is not persisted on unmount
	 */
	const spotlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	useEffect(
		() => () => {
			if (spotlightTimeoutRef.current) clearTimeout(spotlightTimeoutRef.current);
		},
		[],
	);

	const { useChoreographer } = useChoreographerAssets();

	/**
	 * We need to avoid `setState` calls on unmount
	 */
	const hasUnmountedRef = useRef(false);
	/**
	 * {@link startMessage} should be the only way to handle changing the feature discovery state
	 * because it allows us to respect other spotlights and make sure there are no clashes
	 */
	const { startMessage, stopMessage } = useChoreographer(
		READTIME_LOOM_SCRIPT_FEATURE_DISCOVERY_MESSAGE_ID,
		{
			start: useCallback(() => {
				if (hasUnmountedRef.current) return;
				setFeatureDiscoveryState((currentState) => {
					if (currentState === 'hidden') {
						return 'nudge';
					}
					return currentState;
				});
			}, []),
			stop: useCallback(() => {
				if (hasUnmountedRef.current) return;
				setFeatureDiscoveryState('hidden');
			}, []),
		},
	);
	const startMessagePromiseRef = useRef<ReturnType<typeof startMessage> | null>(null);
	const stopMessagePromiseRef = useRef<ReturnType<typeof stopMessage> | null>(null);
	/**
	 * Need this because we want to avoid excessive cleanup function calls
	 */
	const stopMessageRef = useRef(stopMessage);

	useEffect(() => {
		stopMessageRef.current = stopMessage;
	}, [stopMessage]);

	// Determine if we should show the nudge based on eligibility and dismissal state
	const shouldShowNudge = useMemo(() => {
		return (
			isLoomScriptReadTimeExtensionEnabled &&
			isMinimumReadTimeMet &&
			isPageCreatedIn30DaysState &&
			!localStorage.getItemAsBoolean(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
			)
		);
	}, [isLoomScriptReadTimeExtensionEnabled, isMinimumReadTimeMet, isPageCreatedIn30DaysState]);

	const dismissedForCurrentMountRef = useRef(false);
	useEffect(() => {
		/**
		 * We should only allow one promise at a time
		 */
		if (
			featureDiscoveryState === 'hidden' &&
			shouldShowNudge &&
			startMessagePromiseRef.current === null &&
			!dismissedForCurrentMountRef.current
		) {
			startMessagePromiseRef.current = startMessage().finally(() => {
				startMessagePromiseRef.current = null;
			});
		}

		if (
			featureDiscoveryState === 'nudge' &&
			!shouldShowNudge &&
			stopMessagePromiseRef.current === null
		) {
			stopMessagePromiseRef.current = stopMessage().finally(() => {
				stopMessagePromiseRef.current = null;
			});
		}
	}, [shouldShowNudge, featureDiscoveryState, startMessage, stopMessage]);

	/**
	 * Our message should be stopped on unmount to avoid blocking other messages when the context is not active
	 */
	useEffect(
		() => () => {
			hasUnmountedRef.current = true;
			/**
			 * We ONLY want this to run on unmount so we make sure dependencies are empty
			 */
			stopMessageRef.current();
		},
		[],
	);

	const updateLoomScriptReadTimeEligibilityForPage = useCallback(
		(contentNode: any, readTime: number | null, isPageWithLoomVideos: boolean | undefined) => {
			if (
				!contentNode ||
				!isRovoEnabled ||
				isPageWithLoomVideos ||
				readTime === null ||
				!isInterestedInLoom
			) {
				setIsLoomScriptReadTimeExtensionEnabled(false);
				return;
			}

			setIsMinimumReadTimeMet(readTime >= 7);

			// Calculate if page was created within 30 days
			const createdDate = contentNode?.metadata?.createdDate;
			const pageCreatedIn30Days = isPageCreatedIn30Days(createdDate);
			setIsPageCreatedIn30DaysState(pageCreatedIn30Days);
			const contentType = contentNode?.type;
			const isContentCreator =
				'accountId' in (contentNode?.history?.createdBy ?? {}) &&
				contentNode?.history?.createdBy?.accountId === userId;

			const canEdit = contentNode?.operations?.some(
				(op: { operation: string; targetType: any }) =>
					op?.operation === 'update' && op?.targetType === contentType,
			);

			setIsLoomScriptReadTimeExtensionEnabled(
				isContentCreator &&
					contentType === 'page' &&
					(entryPointVariant === 'CO_USE' ||
						entryPointVariant === 'CROSS_FLOW' ||
						entryPointVariant === 'CROSS_JOIN') &&
					canEdit &&
					!isLivePage &&
					isInterestedInLoom,
			);
		},
		[isRovoEnabled, userId, entryPointVariant, isLivePage, isInterestedInLoom],
	);

	const contextValue = useMemo(
		() => ({
			isLoomScriptReadTimeExtensionEnabled,
			isLoomScriptReadTimeButtonNudgeShown: featureDiscoveryState === 'nudge',
			isLoomScriptReadTimeSpotlightShown: featureDiscoveryState === 'spotlight',
			notInterestedInLoomCallback,
			updateLoomScriptReadTimeEligibilityForPage,
			hideLoomScriptReadTimeNudgeAndShowSpotlight: () => {
				const DELAY_MS = 200 as const;
				/**
				 * Prevent excessive timeouts from being created
				 */
				if (spotlightTimeoutRef.current === null && featureDiscoveryState !== 'spotlight') {
					spotlightTimeoutRef.current = setTimeout(() => {
						setFeatureDiscoveryState('spotlight');
						spotlightTimeoutRef.current = null;
					}, DELAY_MS);
				}
			},
			dismissLoomScriptReadTimeSpotlight: () => {
				localStorage.setItem(
					PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_SCRIPT_READTIME_BUTTON_NUDGE_DISMISSED,
					'true',
				);
				/**
				 * No calls should be allowed to `startMessage` as soon as this function is called,
				 * `localStorage.setItem` doesn't seem to update the return value for `localStorage.getItemAsBoolean`
				 * instantly
				 */
				dismissedForCurrentMountRef.current = true;
				stopMessageRef.current();
			},
		}),
		[
			featureDiscoveryState,
			isLoomScriptReadTimeExtensionEnabled,
			notInterestedInLoomCallback,
			updateLoomScriptReadTimeEligibilityForPage,
		],
	);

	return (
		<LoomScriptReadTimeContext.Provider value={contextValue}>
			<RovoChatLoomScriptCtaListener contentId={contentId} />
			{children}
		</LoomScriptReadTimeContext.Provider>
	);
};

export const LoomScriptReadTimeContextProvider = (props: LoomScriptReadTimeContextProviderProps) =>
	props.isLoomAIScriptExperimentEnabled ? (
		<LoomScriptReadTimeEnabledContextProvider {...props} />
	) : (
		props.children
	);
